import React, { useEffect } from 'react';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { Button, Row, Col, Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import CheckboxMarkedCircle from 'mdi-react/CheckboxMarkedCircleIcon';
import CircleOutline from 'mdi-react/CircleOutlineIcon';
import ImageIcon from 'mdi-react/FileImageIcon';
import PlayCircleOutlineIcon from 'mdi-react/PlayCircleOutlineIcon';
import NumericOneCircleOutlineIcon from 'mdi-react/NumericOneCircleOutlineIcon';
import CalendarTodayIcon from 'mdi-react/CalendarTodayIcon';
import CalendarWeekIcon from 'mdi-react/CalendarWeekIcon';
import { connect } from 'react-redux';
import DescriptionComponent from './SpotlightGroupFormDescription';
import renderField from '../../../shared/components/custom/Field';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import validate from './validateGroupForm';
import t, { partial } from '../../../util/translation/translation';
import renderRadioButtonField from '../../../shared/components/form/RadioButton';
import {
  getReadableFormat,
  getReadableSize,
  getSpotlightFormats,
  SPOTLIGHT_SIZES,
  getIconAmountForSizeFormat,
  FormatRatios,
  hasCaption,
} from '../spotlightUtils';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import FormBox from '../../../shared/components/custom/FormBox';
import OptionalColorField from '../../../shared/components/form/OptionalColorField';
import SwitchRow from '../../../shared/components/custom/SwitchRow';
import {
  contrastRatio,
  CONTRAST_GRAPHIC,
  CONTRAST_TEXT,
} from '../../../util/colorContrast';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';

// Formats whose tile text can go on the image instead of below it
const OVERLAY_FORMATS = ['SQUARE', 'LANDSCAPE'];
// The group's own colours, then the tiles' text box (title and text under
// each image)
const GROUP_OWN_COLORS = ['backgroundColor', 'textColor', 'accentColor'];
const GROUP_COLORS = [
  ...GROUP_OWN_COLORS,
  'descriptionBackgroundColor',
  'descriptionTextColor',
];

// Tile drawings for the text position cards: the image, and where its
// text lines go (fill comes from the card's icon colour)
const TextBelowIcon = ({ size }) => (
  <svg width={size + 4} height={size + 4} viewBox="0 0 24 24" aria-hidden>
    <rect x="3" y="2" width="18" height="12" rx="2" opacity="0.35" />
    <rect x="3" y="16.5" width="14" height="2" rx="1" />
    <rect x="3" y="20" width="9" height="2" rx="1" opacity="0.6" />
  </svg>
);
const TextOverlayIcon = ({ size }) => (
  <svg width={size + 4} height={size + 4} viewBox="0 0 24 24" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" opacity="0.35" />
    <rect x="6" y="13.5" width="12" height="2" rx="1" />
    <rect x="6" y="17" width="8" height="2" rx="1" opacity="0.6" />
  </svg>
);

const p = partial('HighlightBannerForm');
const s = partial('shared');

const InnerFormatIcon = ({ format, selected, multiple, last }) => {
  const containerStyle = {
    width: 80,
    height: 142,
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 0 10px 0',
    display: 'flex',
    flexDirection: 'column',
    marginRight: multiple && !last ? 10 : 0,
  };
  const innerContainer = {
    backgroundColor: selected ? '#C3DEFE' : '#D7DAE0',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  };
  switch (format) {
    // A wide cover card with story progress bars
    case 'STORY':
      return (
        <div style={{ ...containerStyle, width: 160 }}>
          <div
            style={{
              ...innerContainer,
              height: 90,
              width: 160,
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 6,
                left: 8,
                right: 8,
                display: 'flex',
                gap: 3,
              }}
            >
              {[0, 1, 2, 3].map((bar) => (
                <div
                  key={bar}
                  style={{
                    flex: 1,
                    height: 3,
                    borderRadius: 2,
                    background: selected ? '#0469DC' : '#9AA0A6',
                    opacity: bar === 0 ? 1 : 0.4,
                  }}
                />
              ))}
            </div>
            <PlayCircleOutlineIcon
              size={26}
              color={selected ? '#0469DC' : 'black'}
            />
          </div>
        </div>
      );
    // A phone with the light on a figure, and what it does
    case 'TAKEOVER':
      return (
        <div
          style={{
            ...containerStyle,
            width: 300,
            flexDirection: 'row',
            gap: 14,
          }}
        >
          <svg width="66" height="120" viewBox="0 0 66 120" aria-hidden>
            <rect
              x="1"
              y="1"
              width="64"
              height="118"
              rx="10"
              fill={selected ? '#121B2B' : '#3A4152'}
            />
            <path
              d="M29 1 L37 1 L60 80 L6 80 Z"
              fill={selected ? '#C3DEFE' : '#D7DAE0'}
              opacity="0.45"
            />
            <circle cx="33" cy="50" r="9" fill="#FFFFFF" />
            <path d="M17 82 C 19 66, 47 66, 49 82 Z" fill="#FFFFFF" />
            <rect
              x="10"
              y="98"
              width="46"
              height="10"
              rx="5"
              fill={selected ? '#0469DC' : '#9AA0A6'}
            />
          </svg>
          <span
            style={{
              flex: 1,
              fontSize: 12,
              textAlign: 'start',
              color: selected ? '#0469DC' : '#646777',
            }}
          >
            {p('takeoverFormatHint')}
          </span>
        </div>
      );
    case 'SQUARE':
      return (
        <div style={containerStyle}>
          <div style={{ ...innerContainer, height: 80, width: 80 }}>
            <ImageIcon size={20} color={selected ? '#0469DC' : 'black'} />
          </div>
        </div>
      );
    case 'LANDSCAPE':
      return (
        <div style={containerStyle}>
          <div style={{ ...innerContainer, height: 45, width: 80 }}>
            <ImageIcon size={20} color={selected ? '#0469DC' : 'black'} />
          </div>
        </div>
      );
    case 'PORTRAIT':
      return (
        <div style={containerStyle}>
          <div style={{ ...innerContainer, height: 142, width: 80 }}>
            <ImageIcon size={20} color={selected ? '#0469DC' : 'black'} />
          </div>
        </div>
      );
    case 'HALF_PORTRAIT':
      return (
        <div style={containerStyle}>
          <div style={{ ...innerContainer, height: 100, width: 80 }}>
            <ImageIcon size={20} color={selected ? '#0469DC' : 'black'} />
          </div>
        </div>
      );
    case 'CIRCLE':
      return (
        <div style={containerStyle}>
          <div
            style={{
              ...innerContainer,
              height: 80,
              width: 80,
              borderRadius: 100,
            }}
          >
            <ImageIcon size={20} color={selected ? '#0469DC' : 'black'} />
          </div>
        </div>
      );
    case 'BANNER':
      return (
        <div style={containerStyle}>
          <div
            style={{
              ...innerContainer,
              height: 25,
              width: 120,
              justifyContent: 'flex-start',
            }}
          >
            <ImageIcon size={20} color={selected ? '#0469DC' : 'black'} />
          </div>
        </div>
      );
    case 'TEXT_BANNER':
      return (
        <div style={containerStyle}>
          <div style={{ flexDirection: 'row', display: 'flex' }}>
            <div
              style={{
                background: selected ? 'rgba(65, 129, 255, 0.12)' : '#D7DAE0',
                height: 36,
                width: 36,
                borderRadius: 36,
                marginRight: 5,
              }}
            />
            <div
              style={{
                ...innerContainer,
                width: 160,
                backgroundColor: selected
                  ? 'rgba(65, 129, 255, 0.12)'
                  : '#D7DAE0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                padding: 10,
                borderRadius: 8,
              }}
            >
              <strong
                style={{
                  color: selected ? '#0f69b4' : 'black',
                  textAlign: 'start',
                }}
              >
                {s('title')}
              </strong>
              <p
                style={{
                  color: selected ? '#0f69b4' : 'black',
                  textAlign: 'start',
                  marginTop: 4,
                }}
              >
                {s('description')}
              </p>
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
};

const FormatComponent = (size, entityExists) => (data) => {
  const {
    fieldValue,
    input: { onChange, value },
  } = data;
  const isSelected = value === fieldValue;
  const iconAmount = getIconAmountForSizeFormat(size, fieldValue);
  // A takeover has no shape of its own: it fills the screen
  const ratio =
    fieldValue === 'TAKEOVER'
      ? getReadableSize('FULL_SCREEN')
      : FormatRatios(fieldValue, size);
  return (
    <Col xs={12} sm={12} md={12} lg={6} className="mt-4">
      <button
        disabled={false}
        style={{
          backgroundColor: isSelected ? '#0469DC10' : 'white',
          borderWidth: 2,
          borderStyle: isSelected ? 'solid' : 'dashed',
          borderRadius: 8,
          borderColor: isSelected ? '#0469DC' : '#D7DAE0',
          opacity: entityExists ? 0.4 : 1,
          width: 360,
          height: 170,
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          padding: 10,
        }}
        onClick={() => onChange(fieldValue)}
        type="button"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <p
            style={{
              color: isSelected ? '#0469DC' : '#D7DAE0',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <strong style={{ color: '#121B2B' }}>{ratio}</strong> -{' '}
            {getReadableFormat(fieldValue)}
          </p>
          {isSelected ? (
            <CheckboxMarkedCircle
              size={12}
              className="icon-min-20"
              color="#0469DC"
            />
          ) : (
            <CircleOutline size={12} className="icon-min-20" color="#D7DAE0" />
          )}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {[...Array(iconAmount).keys()].map((_, idx) => (
            <InnerFormatIcon
              key={idx}
              format={fieldValue}
              selected={isSelected}
              multiple={iconAmount > 1}
              last={idx === iconAmount - 1}
            />
          ))}
        </div>
      </button>
    </Col>
  );
};

const SpotlightGroupForm = ({
  handleSubmit,
  initialize,
  entity,
  spotlightGroupForm,
  change,
}) => {
  useEffect(() => {
    if (entity) {
      initialize({
        title: entity.title,
        format: entity.format,
        size: entity.size,
        // The radio cards work with 'true'/'false'; the API sends a boolean
        descriptionAvailable: String(!!entity.descriptionAvailable),
        hideTitle: !entity.titleVisible,
        subTitle: entity.subTitle,
        customColors: entity.customColors,
        backgroundColor: entity.backgroundColor || '',
        textColor: entity.textColor || '',
        accentColor: entity.accentColor || '',
        descriptionBackgroundColor: entity.descriptionBackgroundColor || '',
        descriptionTextColor: entity.descriptionTextColor || '',
        textPosition: entity.textPosition || 'BELOW',
        // None = once
        takeoverFrequency: entity.takeoverFrequency || 'ONCE',
        // None = opens right away
        takeoverDelaySeconds: entity.takeoverDelaySeconds ?? '',
        backgroundImage: entity.backgroundImageUrl
          ? {
              name: 'group background',
              preview: entity.backgroundImageUrl,
              croppedImage: entity.backgroundImageUrl,
            }
          : undefined,
        headingImage:
          entity.format === 'BANNER' || !entity.imageUrl
            ? undefined
            : {
                name: 'group image',
                preview: entity.imageUrl,
                croppedImage: entity.imageUrl,
              },
      });
    } else {
      initialize({
        format: 'SQUARE',
        size: 'SMALL',
        textPosition: 'BELOW',
        takeoverFrequency: 'ONCE',
        takeoverDelaySeconds: '',
      });
    }
  }, [entity]);
  const handleSizeChange = () => {
    change('format', 'SQUARE');
  };
  const isBannerTextBanner = spotlightGroupForm?.format === 'BANNER';
  // A takeover fills the screen by itself: no size, heading or styling of
  // its own in the feed, only how often it opens
  const isTakeover = spotlightGroupForm?.format === 'TAKEOVER';
  // Readability of the chosen colours (WCAG); only when both are set,
  // since an empty one is the club's colour, which isn't known here
  const groupBackground = spotlightGroupForm?.backgroundColor;
  const textContrast = contrastRatio(
    spotlightGroupForm?.textColor,
    groupBackground
  );
  const accentContrast = contrastRatio(
    spotlightGroupForm?.accentColor,
    groupBackground
  );
  const tileTextContrast = contrastRatio(
    spotlightGroupForm?.descriptionTextColor,
    spotlightGroupForm?.descriptionBackgroundColor
  );
  const contrastWarnings = [
    textContrast !== null &&
      textContrast < CONTRAST_TEXT &&
      p('contrastTextLow', { ratio: textContrast, min: CONTRAST_TEXT }),
    accentContrast !== null &&
      accentContrast < CONTRAST_GRAPHIC &&
      p('contrastAccentLow', { ratio: accentContrast, min: CONTRAST_GRAPHIC }),
  ].filter(Boolean);
  // Quick picks: neutrals, then the group's other colours (to reuse one)
  const colorPresets = [
    ...new Set(
      [
        '#FFFFFF',
        '#000000',
        '#121B2B',
        '#F2F4F7',
        ...GROUP_COLORS.map((name) => spotlightGroupForm?.[name]),
      ]
        .filter((color) => /^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(color || ''))
        .map((color) => color.toUpperCase())
    ),
  ];
  // Text banners have no image, so neither a title image nor image colors.
  const hasTitleImage = spotlightGroupForm?.format !== 'TEXT_BANNER';
  // Text on the image: only where tiles show text (not full screen, and
  // small squares have none)
  const canOverlayText =
    String(spotlightGroupForm?.descriptionAvailable) === 'true' &&
    OVERLAY_FORMATS.includes(spotlightGroupForm?.format) &&
    spotlightGroupForm?.size !== 'FULL_SCREEN' &&
    !(
      spotlightGroupForm?.format === 'SQUARE' &&
      spotlightGroupForm?.size === 'SMALL'
    );
  return (
    <form className="form" onSubmit={handleSubmit}>
      <FormBox title={s('title')} className="mb-3">
        <Container>
          <Row>
            <Col md={12}>
              <div className="form__form-group">
                <span className="form-header">{s('title')}*</span>
                <Field
                  name="title"
                  component={renderField}
                  placeholder={s('title')}
                />
              </div>
            </Col>
          </Row>
          {!isTakeover && (
            <Row>
              <Col>
                <div className="form__form-group-field">
                  <Field
                    type="checkbox"
                    name="hideTitle"
                    disabled={spotlightGroupForm?.format === 'BANNER'}
                    component={renderCheckBoxField}
                    label={t('HighlightBannerPage.hideTitle')}
                    className="colored-click"
                  />
                  &nbsp;
                  <TooltipWithIcon
                    id="no-access-view-tooltip"
                    text={t('HighlightBannerForm.hideTitleInfo')}
                  />
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </FormBox>
      <FormBox title={s('tile')} className="mb-3">
        <Container>
          {!isTakeover && (
            <Row>
              <Col md={12}>
                <span className="form-header">
                  {t('HighlightBannerPage.size')}
                </span>
                <div className="radio-horizontal">
                  {SPOTLIGHT_SIZES.map((size, idx) => (
                    <Col md={5} key={`${size}`}>
                      <div className="form__form-group">
                        <div className="form__form-group-field">
                          <Field
                            name="size"
                            component={renderRadioButtonField}
                            label={getReadableSize(size)}
                            radioValue={size}
                            defaultChecked={idx === 0}
                            onChange={handleSizeChange}
                            disabled={false}
                          />
                        </div>
                      </div>
                    </Col>
                  ))}
                </div>
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <span className="form-header">
                {t('HighlightBannerPage.format')}
              </span>
            </Col>
          </Row>
          <Row style={{ marginTop: -10, paddingBottom: 20 }}>
            {getSpotlightFormats(spotlightGroupForm?.size).map((format) => (
              <Field
                key={format}
                name="format"
                component={FormatComponent(spotlightGroupForm?.size)}
                fieldValue={format}
              />
            ))}
          </Row>
          {hasCaption(spotlightGroupForm?.format, spotlightGroupForm?.size) ? (
            <div style={{ marginTop: 20 }}>
              <Row>
                <Col>
                  <span className="form-header">
                    {t('HighlightBannerPage.imageText')}
                  </span>
                </Col>
              </Row>
              <Row style={{ marginTop: -10, paddingBottom: 20 }}>
                <Field
                  name="descriptionAvailable"
                  component={DescriptionComponent('IMAGE')}
                  fieldValue="false"
                />
                <Field
                  name="descriptionAvailable"
                  component={DescriptionComponent('IMAGE_TEXT')}
                  fieldValue="true"
                />
              </Row>
              {canOverlayText && (
                <div className="form__form-group mb-0">
                  <span className="form-header">{p('textPosition')}</span>
                  <Field
                    name="textPosition"
                    component={ChoiceCards}
                    ariaLabel={p('textPosition')}
                    options={[
                      {
                        value: 'BELOW',
                        icon: TextBelowIcon,
                        label: p('textPosition-BELOW'),
                        hint: p('textPositionHint-BELOW'),
                      },
                      {
                        value: 'OVERLAY',
                        icon: TextOverlayIcon,
                        label: p('textPosition-OVERLAY'),
                        hint: p('textPositionHint-OVERLAY'),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          ) : null}
          {isTakeover && (
            <div className="form__form-group">
              <span className="form-header">{p('takeoverFrequency')}</span>
              <Field
                name="takeoverFrequency"
                component={ChoiceCards}
                ariaLabel={p('takeoverFrequency')}
                columns={3}
                options={[
                  {
                    value: 'ONCE',
                    icon: NumericOneCircleOutlineIcon,
                    label: p('takeoverFrequency-ONCE'),
                    hint: p('takeoverFrequencyHint-ONCE'),
                  },
                  {
                    value: 'DAILY',
                    icon: CalendarTodayIcon,
                    label: p('takeoverFrequency-DAILY'),
                    hint: p('takeoverFrequencyHint-DAILY'),
                  },
                  {
                    value: 'WEEKLY',
                    icon: CalendarWeekIcon,
                    label: p('takeoverFrequency-WEEKLY'),
                    hint: p('takeoverFrequencyHint-WEEKLY'),
                  },
                ]}
              />
            </div>
          )}
          {isTakeover && (
            <Row>
              <Col md={6}>
                <div className="form__form-group mb-0">
                  <label className="form-header" htmlFor="takeoverDelaySeconds">
                    {p('takeoverDelay')}
                  </label>
                  <Field
                    name="takeoverDelaySeconds"
                    component={renderField}
                    type="number"
                    inputMode="numeric"
                    placeholder="0"
                  />
                  <small className="form__form-group-description">
                    {p('takeoverDelayHint')}
                  </small>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </FormBox>
      {!isBannerTextBanner && !isTakeover && (
        <FormBox
          title={
            <>
              {s('styling')}
              <span className="optional-tag">{s('optional')}</span>
            </>
          }
          className="mb-3"
        >
          <div className="spot-style">
            <div className="spot-style__main">
              <div className="form__form-group">
                <span className="form-header">{s('subtitle')}</span>
                <Field
                  name="subTitle"
                  component={renderField}
                  placeholder={s('subtitle')}
                />
              </div>
              {hasTitleImage && (
                <Field
                  name="customColors"
                  component={SwitchRow}
                  label={p('customColors')}
                  description={p('customColorsHint')}
                />
              )}
              {hasTitleImage && spotlightGroupForm?.customColors && (
                <div className="spot-style__colors">
                  <p className="spot-style__hint">{p('groupColorsInfo')}</p>
                  <span className="spot-style__subhead">
                    {p('colorsGroupHeading')}
                  </span>
                  <div className="spot-style__color-grid">
                    {GROUP_OWN_COLORS.map((name) => (
                      <div className="form__form-group mb-0" key={name}>
                        <span className="form-header">{p(name)}</span>
                        <Field
                          name={name}
                          component={OptionalColorField}
                          label={p(name)}
                          clearLabel={p('clearColor')}
                          emptyLabel={p('clubColor')}
                          presets={colorPresets}
                        />
                      </div>
                    ))}
                  </div>
                  {/* How the three colours look together */}
                  <div
                    className="spot-style__sample"
                    style={{
                      background:
                        spotlightGroupForm?.backgroundColor || undefined,
                      color: spotlightGroupForm?.textColor || undefined,
                    }}
                  >
                    <span
                      className="spot-style__sample-dot"
                      style={{
                        background:
                          spotlightGroupForm?.accentColor || undefined,
                      }}
                    />
                    <span
                      className="spot-style__sample-label"
                      style={{
                        color: spotlightGroupForm?.accentColor || undefined,
                      }}
                    >
                      {p('colorSampleLabel')}
                    </span>
                    <span className="spot-style__sample-title">
                      {spotlightGroupForm?.title || p('colorSampleTitle')}
                    </span>
                    {textContrast !== null && (
                      <span
                        className={`spot-style__ratio${
                          textContrast < CONTRAST_TEXT
                            ? ' spot-style__ratio--low'
                            : ''
                        }`}
                        title={p('contrastRatioInfo')}
                      >
                        {textContrast}:1
                      </span>
                    )}
                  </div>
                  {contrastWarnings.map((warning) => (
                    <p
                      key={warning}
                      className="spot-style__warning"
                      role="status"
                    >
                      {warning}
                    </p>
                  ))}

                  {/* The text box under each tile's image (its own colours) */}
                  <div className="spot-style__tile-text">
                    <span className="spot-style__subhead">
                      {p('colorsTileHeading')}
                    </span>
                    <p className="spot-style__hint">{p('colorsTileHint')}</p>
                    <div className="spot-style__tile-row">
                      <div className="spot-style__color-pair">
                        {[
                          [
                            'descriptionBackgroundColor',
                            'colorsTileBackground',
                          ],
                          ['descriptionTextColor', 'colorsTileText'],
                        ].map(([name, short]) => (
                          <div className="form__form-group mb-0" key={name}>
                            <span className="form-header">{p(short)}</span>
                            <Field
                              name={name}
                              component={OptionalColorField}
                              label={p(name)}
                              clearLabel={p('clearColor')}
                              emptyLabel={p('clubColor')}
                              presets={colorPresets}
                            />
                          </div>
                        ))}
                      </div>
                      {/* A tile: its image, then the text box in these colours */}
                      <div className="spot-style__tile-sample" aria-hidden>
                        <span className="spot-style__tile-image" />
                        <span
                          className="spot-style__tile-box"
                          style={{
                            background:
                              spotlightGroupForm?.descriptionBackgroundColor ||
                              undefined,
                            color:
                              spotlightGroupForm?.descriptionTextColor ||
                              undefined,
                          }}
                        >
                          <strong>{p('colorSampleTileTitle')}</strong>
                          <span>{p('colorSampleTileText')}</span>
                        </span>
                      </div>
                    </div>
                    {tileTextContrast !== null &&
                      tileTextContrast < CONTRAST_TEXT && (
                        <p className="spot-style__warning" role="status">
                          {p('contrastTileTextLow', {
                            ratio: tileTextContrast,
                            min: CONTRAST_TEXT,
                          })}
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>
            {hasTitleImage && (
              <div className="spot-style__images">
                <div className="form__form-group">
                  <span className="form-header">{p('titleImage')}</span>
                  <Field
                    saveBase64Cropped
                    name="headingImage"
                    maxSize={0.5}
                    component={renderDropZoneField}
                    displayCrop
                  />
                </div>
                {spotlightGroupForm?.customColors && (
                  <div className="form__form-group">
                    <span className="form-header">{p('backgroundImage')}</span>
                    <Field
                      saveBase64Cropped
                      name="backgroundImage"
                      maxSize={1}
                      component={renderDropZoneField}
                      // It covers the whole group, whatever its shape, so
                      // any crop (the default is a locked square)
                      crop="FREE_CROP"
                      displayCrop
                    />
                    <span className="spot-style__hint">
                      {p('backgroundImageInfo')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </FormBox>
      )}
      <Container>
        <Row>
          <Col>
            <Button type="submit" color="primary">
              {entity ? t('shared.save') : t('shared.create')}
            </Button>
            <Link to="/spotlight">
              <Button>{t('shared.cancel')}</Button>
            </Link>
          </Col>
        </Row>
      </Container>
    </form>
  );
};
const reduxF = reduxForm({
  form: 'spotlightGroupForm',
  validate,
});
const mapStateToProps = (state) => ({
  spotlightGroupForm: getFormValues('spotlightGroupForm')(state),
});
export default connect(mapStateToProps)(reduxF(SpotlightGroupForm));
